import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import fr from 'facet-rest';
import { useSnackbarContext } from 'Context/SnackbarContext';
import useMessagesStatsContext from 'Context/MessagesStatsContext';
import { QueryVal, removeQuery } from 'Helpers/Query';
import { pickWithPriority } from 'Helpers/Affordances';

const CommentLayoutContext = React.createContext(undefined);

// Actions
const SENDING_MESSAGE = 'sending-message';
const DONE_SENDING_MESSAGE = 'done-sending-message';
const TOGGLE_SHOW_COMMENTS = 'toggle-comments';
const START_LOADING_THREAD_COLLECTION = 'start-loading-thread-collection';
const SET_THREAD_COLLECTION = 'set-thread-collection';
const SET_MESSAGE_RESOURCE_LIST = 'set-message-resource-list';

const init = () => {
  const showComments = QueryVal('showComments', false);
  return {
    showComments,
    threadCollectionLoading: null,
    threadCollection: null,
    inflightMessage: null,
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case TOGGLE_SHOW_COMMENTS: {
      return {
        ...state,
        showComments: !state.showComments,
      };
    }
    case SENDING_MESSAGE: {
      return {
        ...state,
        inflightMessage: action.messageResource,
      };
    }
    case DONE_SENDING_MESSAGE: {
      return {
        ...state,
        inflightMessage: null,
      };
    }
    case START_LOADING_THREAD_COLLECTION: {
      return {
        ...state,
        threadCollectionLoading: true,
        messageResourceListLoading: true,
      };
    }
    case SET_THREAD_COLLECTION: {
      return {
        ...state,
        threadCollectionLoading: null,
        threadCollection: action.threadCollection,
        threadResource: action.threadCollection?.resources().head(),
      };
    }
    case SET_MESSAGE_RESOURCE_LIST: {
      return {
        ...state,
        messageResourceListLoading: null,
        messageResourceList: action.messageResourceList,
      };
    }
    default: {
      throw new Error(`Unhandled CommentLayoutContext action type: ${action.type}`);
    }
  }
};

const CommentLayoutProvider = ({
  children,
}) => {
  const { handleMessagesStatsRefresh } = useMessagesStatsContext();
  const { pageContentResource } = usePageContentContext();
  const { displayErrorMessage } = useSnackbarContext();
  const mountedRef = React.useRef(null);
  const endRef = React.useRef(null);
  const showCommentsInit = QueryVal('showComments');
  const [state, dispatch] = React.useReducer(reducer, { showCommentsInit }, init);

  const dispatchIfMounted = (args) => {
    if (mountedRef.current === true) {
      return dispatch(args);
    }

    return undefined;
  };

  const toggleShowComments = React.useCallback(() => {
    if (state.showComments) {
      mountedRef.current = false;
    }

    dispatch({ type: TOGGLE_SHOW_COMMENTS });
  }, [state]);

  const messageResourceListPicker = (resource) => {
    return pickWithPriority(resource, [
      [fr.relationships.messages.name, async () => {
        const messagesList = await resource.relationship(fr.relationships.messages.name).link('related').get();
        // Must get rid of first message for the list, because the first message is the title of the proposal, not a comment.
        return messagesList?.resources().tail();
      }],
    ], () => {
      return resource?.attr('replies')?.resources().list().reverse();
    })();
  };

  const loadThreadCollectionLink = async (link) => {
    mountedRef.current = true;
    dispatch({ type: START_LOADING_THREAD_COLLECTION });
    try {
      const resp = await link.get();

      const [threadResource] = resp;
      dispatchIfMounted({ type: SET_THREAD_COLLECTION, threadCollection: resp });

      const resourceToSet = await messageResourceListPicker(threadResource);

      dispatch({ type: SET_MESSAGE_RESOURCE_LIST, messageResourceList: resourceToSet });
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const scrollToBottomOfMessages = (args = {}) => {
    const { isSmooth } = args;
    if (endRef.current) {
      const options = isSmooth ? { behavior: 'smooth' } : undefined;
      endRef.current.scrollIntoView(options);
    }
  };

  const draftMessagePicker = (resource) => {
    return pickWithPriority(resource, [
      [fr.relationships.draftMessages.name, async () => {
        let draftMessageCollection = await resource.relationship(fr.relationships.draftMessages.name).link('related').get();
        if (!draftMessageCollection.resources().head()) {
          const [newDraftMessage] = draftMessageCollection.create({ addToCollection: true });
          newDraftMessage.attr('isDraft', 'true');
          draftMessageCollection = await newDraftMessage.submit();
        }
        return draftMessageCollection.resources().head();
      }],
    ], () => {
      const [draftMessage] = resource.attr('draftMessage');
      return draftMessage;
    })();
  };

  const handleSendMessage = React.useCallback(async (text, setText) => {
    if (!state.threadCollection) {
      return null;
    }

    try {
      const [threadResource] = state.threadCollection;

      const draftMessage = await draftMessagePicker(threadResource);

      draftMessage.attr('content', text);

      dispatch({ type: SENDING_MESSAGE, messageResource: draftMessage.copy() });
      setText('');
      await draftMessage.attr('isDraft', 'false').submit();
      handleMessagesStatsRefresh();
      await loadThreadCollectionLink(state.threadCollection.link('self'));
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
    dispatch({ type: DONE_SENDING_MESSAGE });

    return null;
  }, [state]);

  useComponentDidMount(() => {
    const relationship = pageContentResource.relationship(fr.relationships.facetPlanCommentThreads.name);

    if (!relationship) {
      return null;
    }

    return loadThreadCollectionLink(relationship.link('related'));
  }, () => {
    removeQuery('showComments');
  });

  const memoizedValue = React.useMemo(() => {
    return {
      showCommentLayout: state.showComments,
      inflightMessage: state.inflightMessage,
      sendingMessage: state.inflightMessage !== null,
      threadCollection: state.threadCollection,
      threadResource: state.threadResource,
      threadCollectionLoading: state.threadCollectionLoading,
      messageResourceList: state.messageResourceList,
      messageResourceListLoading: state.messageResourceListLoading,
      loadThreadCollectionLink,
      toggleShowComments,
      handleSendMessage,
      endRef,
      scrollToBottomOfMessages,
    };
  }, [
    state,
    loadThreadCollectionLink,
    toggleShowComments,
    handleSendMessage,
    endRef,
    scrollToBottomOfMessages,
  ]);

  return (
    <CommentLayoutContext.Provider value={memoizedValue}>
      {children}
    </CommentLayoutContext.Provider>
  );
};

// Use CommentLayout State Context
const useCommentLayoutContext = () => {
  const context = React.useContext(CommentLayoutContext);
  if (context === undefined) {
    throw new Error('useCommentLayoutContext must be used within a CommentLayoutProvider');
  }
  return context;
};

CommentLayoutProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  CommentLayoutProvider,
  useCommentLayoutContext,
};
